import { useMediaQuery } from '@material-ui/core';
import clsx from 'clsx';
import { Placement } from '@popperjs/core';
import { ReactNode, SyntheticEvent, useState } from 'react';
import { createPortal } from 'react-dom';
import { usePopper } from 'react-popper';
import { Arrow } from './Arrow';
import { Button } from '../Button/NewButton';

export interface PopperProps extends BaseProps {
  anchorEl: Element | null;
  children: ReactNode;
  color?: PopperColors;
  clickAway?: boolean;
  isVisible: boolean;
  offsetX?: number;
  offsetY?: number;
  onClose?(event: SyntheticEvent): void;
  placement: Placement;
  parentPopperElementId?: string;
  showArrow?: boolean;
  mobileFullscreen?: boolean;
}

export type PopperColors = 'background' | 'card' | 'default';

export const Popper = ({
  anchorEl,
  children,
  className,
  clickAway = false,
  color = 'default',
  isVisible,
  offsetX = 0,
  offsetY = 0,
  onClose = () => null,
  placement,
  parentPopperElementId = '',
  showArrow = false,
  style,
  mobileFullscreen = false,
  ...rest
}: PopperProps) => {
  const [popperElement, setPopperElement] = useState<HTMLDivElement | null>(
    null
  );
  const [arrowElement, setArrowElement] = useState<HTMLDivElement | null>(null);

  const isMobile = useMediaQuery('(max-width: 640px)');

  const { state, styles } = usePopper(anchorEl, popperElement, {
    modifiers: [
      { name: 'arrow', options: { element: arrowElement } },
      { name: 'offset', options: { offset: [offsetX, offsetY] } },
    ],
    placement: placement,
  });
  const classes = useStyles();

  return isVisible
    ? createPortal(
        <>
          {clickAway && (
            <div className="fixed inset-0 z-[9997]" onClick={onClose} />
          )}
          {(!mobileFullscreen || (mobileFullscreen && !isMobile)) && (
            <div
              className={clsx(
                'z-[9998] w-fit h-fit rounded shadow-lg',
                classes.color[color],
                className,
                { 'hidden sm:block': mobileFullscreen }
              )}
              ref={setPopperElement}
              style={style ? { ...styles.popper, ...style } : styles.popper}
              {...rest}
            >
              <Arrow
                color={color}
                placement={state?.placement ?? placement}
                ref={setArrowElement}
                show={showArrow}
                style={styles.arrow}
              />
              <div className="flex" style={styles.offset}>
                {children}
              </div>
            </div>
          )}

          {mobileFullscreen && isMobile && (
            <div
              className={clsx(
                'fixed inset-0 block sm:hidden z-[9998]',
                classes.color[color],
                className
              )}
              {...rest}
            >
              <div className="h-[calc(100dvh-70px)]">{children}</div>
              <div className="p-4 w-full border-t tg-border-color">
                <Button fullWidth typeCasing="capitalize" onClick={onClose}>
                  Close
                </Button>
              </div>
            </div>
          )}
        </>,
        document.getElementById(parentPopperElementId) ?? document.body
      )
    : null;
};

const useStyles = () => ({
  color: {
    background: 'bg-background-light dark:bg-background-dark',
    card: 'bg-card-light dark:bg-card-dark',
    default: 'bg-[#616161]',
  },
});
